import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, ValidationErrors } from 'webapp-types';
import { validateByRules } from 'webapp/utils/validate';

import FormEmailRecipientsComponent from 'webapp/components/form/email-recipients';
import FormInputComponent from 'webapp/components/form/input';
import FormTextareaComponent from 'webapp/components/form/textarea';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type ReportDelivery from 'webapp/models-custom/report-delivery';

interface Args {
	model: ReportDelivery;
	onContentChange: () => void;
	onClose: () => void;
}

export default class ReportsArchiveResendFormComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;

	@tracked submitInProgress = false;
	@tracked errors: ValidationErrors<string> = {};

	model: {
		isNew: boolean;
		recipients: string[];
		subject: string;
		note: string;
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.model = {
			isNew: true,
			recipients: Array.from(this.args.model.recipients),
			subject: '',
			note: '',
		};
	}

	validate(): boolean {
		this.errors = validateByRules(this.model, ['required'], 'recipients');
		return Object.keys(this.errors).length === 0;
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		if (!this.validate()) {
			return;
		}

		this.submitInProgress = true;

		const layer = this.infolayerService.loading('Preparing...');

		const data = {
			recipients: this.model.recipients,
			subject: this.model.subject,
			note: this.model.note,
		};

		this.requestService
			.api('POST', `/report/deliveries/${this.args.model.id}/resend`, data)
			.then(
				() => {
					layer.ok('Sent!');
					next(this, this.close);
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	close(): void {
		this.args.onClose();
	}
	<template>
		<FormEmailRecipientsComponent
			@model={{this.model}}
			@submitInProgress={{this.submitInProgress}}
			@onContentChange={{@onContentChange}}
			@errors={{this.errors}}
		/>

		<div class="row">
			<FormInputComponent
				name="subject"
				placeholder="Add a custom subject (optional)"
				disabled={{this.submitInProgress}}
				maxlength="512"
				@value={{this.model.subject}}
			/>
		</div>

		<div class="row note">
			<FormTextareaComponent
				name="note"
				placeholder="Add a personal note (optional)"
				disabled={{this.submitInProgress}}
				maxlength="5000"
				@value={{this.model.note}}
			/>
		</div>

		<div class="controls2">
			<div class="controls2__right">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text="Send"
					@onTrigger={{this.save}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>
		</div>
	</template>
}
